import React, { useCallback, useState, useEffect } from 'react';
import { Box, Typography, Tooltip, TextField, InputAdornment, useTheme } from '@material-ui/core';
import {
  AttachMoney as MoneyIcon,
  CreditCard as CardIcon,
} from '@material-ui/icons';
import PixIcon from '@mui/icons-material/Pix';
import { usePdvStyles } from '../styles/pdvStyles';
import clsx from 'clsx';

const PAYMENT_METHODS = [
  { id: 'money', label: 'Dinheiro', icon: MoneyIcon },
  { id: 'credit', label: 'Crédito', icon: CardIcon },
  { id: 'debit', label: 'Débito', icon: CardIcon },
  { id: 'pix', label: 'PIX', icon: PixIcon },
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);
};

const PdvPaymentSelector = ({
  selectedMethod = 'money',
  selectedIndex = 0,
  onSelectMethod,
  isActive = false,
  total = 0,
  changeAmount = 0,
  onChangeAmountChange,
}) => {
  const classes = usePdvStyles();
  const theme = useTheme();
  const [changeInput, setChangeInput] = useState('');

  // Reset change input when payment method changes
  useEffect(() => {
    if (selectedMethod !== 'money') {
      setChangeInput('');
      if (onChangeAmountChange) {
        onChangeAmountChange(0);
      }
    }
  }, [selectedMethod, onChangeAmountChange]);

  const handleSelect = useCallback(
    (methodId) => {
      onSelectMethod(methodId);
    },
    [onSelectMethod]
  );

  const handleChangeInputChange = (e) => {
    const value = e.target.value.replace(/[^0-9,]/g, '').replace(',', '.');
    setChangeInput(e.target.value.replace(/[^0-9,]/g, ''));

    const numericValue = parseFloat(value) || 0;
    if (onChangeAmountChange) {
      onChangeAmountChange(numericValue);
    }
  };

  const changeToGive = changeAmount > total ? changeAmount - total : 0;

  return (
    <Box className={classes.paymentSelector} style={{ marginBottom: 0 }}>
      <Typography variant="caption" style={{ fontWeight: 600, color: theme.palette.text.secondary, marginBottom: 6, display: 'block' }}>
        Forma de Pagamento
      </Typography>
      <Box className={classes.paymentOptions}>
        {PAYMENT_METHODS.map((method, index) => {
          const IconComponent = method.icon;
          const isSelected = selectedMethod === method.id;

          return (
            <Tooltip key={method.id} title={method.label}>
              <Box
                className={clsx(classes.paymentOption, {
                  [classes.paymentOptionSelected]: isSelected,
                })}
                onClick={() => handleSelect(method.id)}
                data-payment-index={index}
              >
                <Box className={classes.paymentOptionIcon}>
                  {IconComponent ? (
                    <IconComponent fontSize="inherit" />
                  ) : null}
                </Box>
                <Typography className={classes.paymentOptionLabel}>
                  {method.label}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* Change calculation for cash payment - compact */}
      {selectedMethod === 'money' && (
        <Box style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
          <TextField
            size="small"
            variant="outlined"
            label="Recebido"
            placeholder="0,00"
            value={changeInput}
            onChange={handleChangeInputChange}
            InputProps={{
              startAdornment: <InputAdornment position="start">R$</InputAdornment>,
            }}
            style={{ flex: 1 }}
          />
          {changeToGive > 0 && (
            <Box
              style={{
                padding: '6px 10px',
                backgroundColor: theme.palette.success.main + '15',
                borderRadius: 6,
                border: `1px solid ${theme.palette.success.main}`,
                whiteSpace: 'nowrap',
              }}
            >
              <Typography variant="caption" style={{ color: theme.palette.success.main, fontWeight: 600 }}>
                Troco: <span style={{ color: theme.palette.success.dark, fontWeight: 700 }}>{formatCurrency(changeToGive)}</span>
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default React.memo(PdvPaymentSelector);
